<script setup>
import { onMounted, ref, useTemplateRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getPollTemplate } from '@/services/pollTemplateService.js'
import { editQuestion, getQuestion, newQuestion } from '@/services/questionService.js'
import { sanitizeNumericInput } from '@/libs/utils/questions/numeric.js'
import {
  cloneOptions,
  addOption,
  validateProperty,
  requiredProperty
} from '@/components/PollTemplate/Common/utilities.js'

import ButtonSalva from '@/components/Generics/Buttons/ButtonSalva.vue'
import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'

import TranslationProvider from '@/provider/translations.js'
import { toast } from '@/services/toast.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { defaultProperties, validateNumberQuestion } from '@/components/PollTemplate/Common/utilities'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCopy, faXmark, faSpinner, faPlus } from '@fortawesome/free-solid-svg-icons'

library.add(faSpinner, faCopy, faXmark, faPlus)

const props = defineProps(['updatePageTitle'])
const router = useRouter()
const route = useRoute()

const currentQuestionRequired = defineModel('currentQuestionRequired')
const currentQuestionType = defineModel('currentQuestionType')
const currentQuestionText = defineModel('currentQuestionText')
const currentQuestionHelper = defineModel('currentQuestionHelper')
const currentQuestionHasNote = defineModel('currentQuestionHasNote')

const currentQuestionNumericMinRange = defineModel('currentQuestionNumericMinRange')
const currentQuestionNumericMaxRange = defineModel('currentQuestionNumericMaxRange')

const currentQuestionTextError = ref('')
const currentQuestionTypeError = ref('')

const pollTemplate = ref(null)
const loading = ref(true)
const confirmDeleteResponse = ref(null)
const revisionAlert = ref(false)
const revisionMessage = ref('Il salvataggio incrementerà il numero di revisione del questionario')
const responseNewProperties = ref([])
const questionGroup = ref({})
const groups = ref([])
const additionalProperties = useTemplateRef('additional-property')
const readOnly = ref(false)
const canView = ref(false)

const customAnswersTypes = ref([
  'radio',
  'select',
  'checkbox',
  'numeric'
])

const currentQuestion = ref({
  'parentQuestionGroup': null,
  'text': '',
  'type': 'text',
  'helper': '',
  'position': 0,
  'required': false,
  'hasNote': false,
  'additionalProperties': {
    'range': {},
    'select_values': []
  }
})

onMounted(async () => {
  canView.value = route.meta?.permissions?.canView ?? false

  if (!canView.value) {
    return
  }

  pollTemplate.value = await getPollTemplate(route.params.id, { complete: 1 })
  loading.value = false

  if (route.query.idQuestion) {
    props.updatePageTitle(`Modifica domanda - Template ${pollTemplate.value.title}`)

    currentQuestion.value = await getQuestion(route.query.idQuestion)
    if (currentQuestion.value.type === 'numeric') {
      currentQuestionNumericMinRange.value = currentQuestion.value.additionalProperties.range?.min ?? ''
      currentQuestionNumericMaxRange.value = currentQuestion.value.additionalProperties.range?.max ?? ''
    }

    currentQuestionText.value = currentQuestion.value.text
    currentQuestionRequired.value = currentQuestion.value.required
    currentQuestionType.value = currentQuestion.value.type
    currentQuestionHelper.value = currentQuestion.value.helper
    currentQuestionHasNote.value = currentQuestion.value.hasNote
    questionGroup.value = pollTemplate.value.questionGroups.find(group => group.id === currentQuestion.value.parentQuestionGroup)
    readOnly.value = !currentQuestion.value['enabledActions']['update']
  } else {
    props.updatePageTitle(`Crea domanda - Template ${pollTemplate.value.title}`)
    questionGroup.value = pollTemplate.value.questionGroups.find(group => group.id === route.query.idGroup)
    currentQuestion.value.position = questionGroup.value.questions.length + 1 ?? 0
    currentQuestion.value.parentQuestionGroup = questionGroup.value.id
  }
  loading.value = false
})

function addResponse() {
  const response = addOption()
  currentQuestion.value.additionalProperties.select_values.push(response)

  responseNewProperties.value[response.value + '_key'] = ''
  responseNewProperties.value[response.value + '_value'] = ''
  responseNewProperties.value[response.value + '_error'] = false
  responseNewProperties.value[response.value + '_errorMessage'] = ''
}

function addResponseProperty(response, responseValue) {
  responseNewProperties.value[responseValue + '_error'] = false
  if (!responseNewProperties.value[responseValue + '_key']) {
    responseNewProperties.value[responseValue + '_error'] = true
    responseNewProperties.value[responseValue + '_errorMessage'] = 'Inserire il nome della proprietà'
    return
  }
  if (!response.properties) {
    response.properties = []
  }
  for (const property of response.properties) {
    if (property.key.trim() === responseNewProperties.value[responseValue + '_key'].trim()) {
      responseNewProperties.value[responseValue + '_error'] = true
      responseNewProperties.value[responseValue + '_errorMessage'] = 'Proprietà con questa chiave già presente'
      return
    }
  }
  response.properties.push({
    'key': responseNewProperties.value[responseValue + '_key'],
    'value': responseNewProperties.value[responseValue + '_value']
  })
  responseNewProperties.value[responseValue + '_key'] = ''
  responseNewProperties.value[responseValue + '_value'] = ''
  responseNewProperties.value[responseValue + '_type'] = 'text'
}

function removeResponseProperty(property, response) {
  response.properties.splice(response.properties.indexOf(property), 1)
}

function checkPropertyAttr(property, attrName, defaultValue) {
  if (defaultProperties.length) {
    for (const defaultProperty of defaultProperties) {
      if (defaultProperty['key'] === property) {
        return defaultProperty[attrName]
      }
    }
  }

  return defaultValue
}

function removeResponse(response) {

  let childGroup = checkHasChildGroup(response)

  if (childGroup) {
    if (confirmDeleteResponse.value && confirmDeleteResponse.value === response.value) {
      currentQuestion.value.additionalProperties.select_values.forEach((el, index) => {
        if (el && response && el.value === response.value) {
          removeGroup(childGroup)
          currentQuestion.value.additionalProperties.select_values.splice(index, 1)
        }
      })
      confirmDeleteResponse.value = null
    } else {
      confirmDeleteResponse.value = response
    }
  } else {

    currentQuestion.value.additionalProperties.select_values.splice(currentQuestion.value.additionalProperties.select_values.indexOf(response), 1)
    confirmDeleteResponse.value = null
  }
}

function checkHasChildGroup(response) {
  let childGroup = null
  groups.value.forEach((group) => {
    if (group.dependentQuestionCondition && group.dependentQuestionCondition === response.value) {
      childGroup = group
    }
  })

  return childGroup
}

function removeGroup(group) {
  groups.value.splice(groups.value.indexOf(group), 1)
}

function validateQuestion() {

  currentQuestionTextError.value = ''
  currentQuestionTypeError.value = ''

  if (!questionGroup.value.id
      || !currentQuestionText.value?.trim()
      || !currentQuestionType.value?.trim()
  ) {
    if (!currentQuestionText.value?.trim()) {
      currentQuestionTextError.value = TranslationProvider.getTrans('app.error.defaultProperty.value.mandatory')
    }

    if (!currentQuestionType.value?.trim()) {
      currentQuestionTypeError.value = TranslationProvider.getTrans('app.error.defaultProperty.value.mandatory')
    }

    toast.create('Ricontrolla i dati e riprova', 'error')
    return
  }

  if (customAnswersTypes.value.includes(currentQuestionType.value)) {
    if (currentQuestionType.value !== 'numeric' && currentQuestion.value.additionalProperties.select_values.length <= 0) {
      toast.create('Risposte alla domanda non presenti, inserirle e riprovare', 'error')
      return
    }
  }

  groups.value.forEach((el, idx) => {
    if (!el.dependentQuestionCondition || el.dependentQuestionCondition.trim() === '') {
      toast.create('Gruppo n.' + (idx + 1) + ' non contiene una condizione valida')
    }
  })

  currentQuestion.value.text = currentQuestionText.value
  currentQuestion.value.type = currentQuestionType.value
  currentQuestion.value.helper = currentQuestionHelper.value ?? ''
  currentQuestion.value.required = currentQuestionRequired.value ?? false
  currentQuestion.value.hasNote = currentQuestionHasNote.value ?? false

  if (currentQuestion.value.type === 'numeric') {
    const range = validateNumberQuestion(currentQuestionNumericMinRange.value, currentQuestionNumericMaxRange.value)
    if (range === false) {
      return
    } else {
      currentQuestion.value.additionalProperties.range = range
    }
  }

  if (
    currentQuestion.value.type === 'checkbox' ||
    currentQuestion.value.type === 'radio' ||
    currentQuestion.value.type === 'select'
  ) {
    let error = false
    let errorLabelMissing = false
    let additionalPropertiesMessages = []

    if (currentQuestion.value.additionalProperties.select_values.length) {
      currentQuestion.value.additionalProperties.select_values.forEach(response => {

        if (!response.label) {
          error = true
          errorLabelMissing = true
        }

        response?.properties.forEach(option => {
          try {
            option.value = validateProperty(option)
          } catch (e) {
            if (!additionalPropertiesMessages.includes(e.message)) {
              additionalPropertiesMessages.push(e.message)
            }

            for (const ap of additionalProperties.value) {
              if (ap.classList.contains('is-invalid')) {
                const y = ap.getBoundingClientRect().top + window.scrollY
                window.scroll({
                  top: y - 500,
                  behavior: 'smooth'
                })
                break
              }
            }
            error = true
          }
        })
      })
    }
    if (error) {
      if (errorLabelMissing) {
        toast.create(TranslationProvider.getTrans('app.error.label.value.missing'), 'error')
      }

      for (const message of additionalPropertiesMessages) {
        toast.create(message, 'error')
      }

      return
    }
  }

  if (pollTemplate.value.state === 'active' && revisionAlert.value) {
    if (currentQuestion.value.id) {
      confirmModal('Incremento numero revisione', 'La modifica della domanda incrementerà il numero di revisione, procedere?', handleQuestion, currentQuestion.value)
    } else {
      confirmModal('Incremento numero revisione', 'La creazione incrementerà il numero di revisione, procedere?', handleQuestion, currentQuestion.value)
    }
  } else {
    handleQuestion(currentQuestion.value)
  }
}

async function handleQuestion() {

  if (!customAnswersTypes.value.includes(currentQuestionType.value)) {
    currentQuestion.value.additionalProperties.select_values = []
    currentQuestion.value.additionalProperties.range = {}
  } else {
    if (currentQuestionType.value === 'numeric') {
      currentQuestion.value.additionalProperties.select_values = []
    } else {
      currentQuestion.value.additionalProperties.range = {}
    }
  }

  let $method = currentQuestion.value.id ? 'put' : 'post'

  try {
    loading.value = true
    if ($method === 'post') {
      await newQuestion(currentQuestion.value)
    } else {
      await editQuestion(currentQuestion.value)
    }
    loading.value = false
    router.back()
  } catch (error) {
    loading.value = false
  }
}

</script>

<template>

  <div class="card card-add-question shadow-none">
    <div v-if="!loading" class="card-body">
      <div>
        <div class="mb-3">
          <label for="questionTitle" class="form-label">
            Titolo *
          </label>
          <input :disabled="readOnly"
                 :readonly="readOnly"
                 type="text"
                 class="form-control"
                 :class="{'is-invalid': currentQuestionTextError }"
                 id="questionTitle"
                 v-model="currentQuestionText"
                 required
          >
          <div class="text-danger" v-if="currentQuestionTextError">
            {{currentQuestionTextError}}
          </div>
        </div>

        <div class="mb-3">
          <div class="form-check form-switch">
            <label class="form-check-label" for="questionRequired">
              Obbligatoria *
            </label>
            <input :disabled="readOnly"
                   :readonly="readOnly"
                   class="form-check-input"
                   type="checkbox"
                   role="switch"
                   id="questionRequired"
                   v-model="currentQuestionRequired"
            >
          </div>
        </div>

        <div class="mb-3">
          <label for="questionType" class="form-label">Tipo domanda *</label>
          <select :disabled="readOnly"
                  class="form-select"
                  aria-label="Tipo domanda"
                  v-model="currentQuestionType"
                  :class="{'is-invalid': currentQuestionTypeError }"
          >
            <option selected="selected" value="text">Aperta</option>
            <option value="select">Scelta singola</option>
            <option value="checkbox">Checkbox</option>
            <option value="radio">Radio</option>
            <option value="numeric">Numerica</option>
          </select>

          <div class="text-danger" v-if="currentQuestionTypeError">
            {{currentQuestionTypeError}}
          </div>
        </div>

        <div class="mb-3">
          <label for="questionHelper" class="form-label">Helper</label>
          <input :disabled="readOnly" :readonly="readOnly" type="text" class="form-control" id="questionHelper"
                 v-model="currentQuestionHelper">
        </div>

        <div class="mb-3">
          <div class="form-check form-switch">
            <label class="form-check-label" for="questionNote">Mostra campo note</label>
            <input :disabled="readOnly" :readonly="readOnly" class="form-check-input" type="checkbox" role="switch"
                   id="questionNote" v-model="currentQuestionHasNote">
          </div>
        </div>

        <div v-if="customAnswersTypes.includes(currentQuestionType)">
          <hr>

          <div v-if="currentQuestionType === 'numeric'">
            <h3>Intervallo di valori consentiti:</h3>

            <div class="d-flex flex-wrap">
              <div class="mb-3 col-12 col-md-6 pe-md-2">
                <label for="minRange" class="form-label">
                  Valore minimo
                </label>
                <input :disabled="readOnly"
                       :readonly="readOnly"
                       type="number"
                       class="form-control"
                       id="minRange"
                       @keydown="sanitizeNumericInput($event, currentQuestionNumericMinRange)"
                       v-model="currentQuestionNumericMinRange"
                >
              </div>

              <div class="mb-3 col-12 col-md-6">
                <label for="maxRange" class="form-label">
                  Valore massimo
                </label>
                <input :disabled="readOnly"
                       :readonly="readOnly"
                       type="number"
                       class="form-control"
                       id="maxRange"
                       @keydown="sanitizeNumericInput($event, currentQuestionNumericMinRange)"
                       v-model="currentQuestionNumericMaxRange"
                >
              </div>
            </div>
          </div>

          <div class="mb-3" v-else>
            <div v-if="currentQuestion.additionalProperties.select_values.length > 0" class="container-fluid">
              <div class="row px-2 py-3 mb-2"
                   v-for="(response, index) in currentQuestion.additionalProperties.select_values" :key="index">
                <div class="d-flex px-0" v-if="!confirmDeleteResponse">
                  <div class="flex-fill pe-2">
                    <div>
                      <label :for="'questionResponseLabel'+index+'Label'"
                             class="form-label text-nowrap me-1 align-middle mb-0 pt-2"
                      >
                        Etichetta *
                      </label>
                      <input :disabled="readOnly"
                             :readonly="readOnly"
                             type="text"
                             :class="{ 'is-invalid': !response.label }"
                             class="form-control me-1"
                             :id="'questionResponseLabel'+index+'Label'"
                             v-model="response.label"
                             required
                      >
                      <div v-if="!response.label" class="invalid-feedback">
                        {{ TranslationProvider.getTrans('app.error.defaultProperty.value.mandatory') }}
                      </div>
                    </div>

                    <div class="mt-2">
                      <p class="mb-1">Proprietà</p>
                      <template v-if="response.properties">
                        <div v-for="(property, i) in response.properties" :key="i">
                          <div class="input-group mb-3">
                            <input v-model="property.key"
                                   :disabled="readOnly"
                                   type="text"
                                   class="form-control"
                                   :class="{
                                      'is-invalid': requiredProperty(property)
                                    }"
                                   :readonly="readOnly || checkPropertyAttr(property.key, 'readonly', false)"
                                   placeholder="Nome proprietà"
                                   aria-label="Nome proprietà"
                                   aria-describedby="button-add"
                            >
                            <input
                              v-model="property.value"
                              :type="checkPropertyAttr(property.key, 'type', 'text')"
                              :required="checkPropertyAttr(property.key, 'required', false)"
                              class="form-control"
                              :class="{
                                'is-invalid rounded-end': requiredProperty(property)
                              }"
                              :placeholder="'Valore ' + (checkPropertyAttr(property.key, 'required', false) ? '*' : '')"
                              aria-label="Valore"
                              :disabled="readOnly" :readonly="readOnly"
                              aria-describedby="button-add"
                              ref="additional-property"

                            >
                            <div class="input-group-append" v-if="checkPropertyAttr(property.key, 'canDelete', true)">
                              <button v-if="!readOnly" class="btn btn-outline-danger h-100 rounded-0 rounded-end"
                                      @click="removeResponseProperty(property, response)"
                                      type="button" id="button-add">
                                Elimina
                              </button>
                            </div>
                            <div v-if="requiredProperty(property)" class="invalid-feedback">
                              {{ TranslationProvider.getTrans('app.error.defaultProperty.value.mandatory') }}
                            </div>
                          </div>
                        </div>
                      </template>

                      <div class="input-group" v-if="!readOnly">
                        <input v-model="responseNewProperties[response.value+'_key']"
                               :type="responseNewProperties[response.value+'_type']" class="form-control"
                               placeholder="Nome proprietà" aria-label="Nome proprietà" aria-describedby="button-add">
                        <input v-model="responseNewProperties[response.value+'_value']"
                               :type="responseNewProperties[response.value+'_type']" class="form-control"
                               placeholder="Valore" aria-label="Valore" aria-describedby="button-add">
                        <div class="input-group-append">
                          <button class="btn btn-outline-primary h-100 rounded-0 rounded-end"
                                  @click="addResponseProperty(response, response.value)"
                                  type="button" id="button-add">
                            Aggiungi
                          </button>
                        </div>
                      </div>
                      <p v-if="responseNewProperties[response.value+'_error']" class="text-danger mb-0">
                        {{ responseNewProperties[response.value + '_errorMessage'] }}</p>
                    </div>
                  </div>

                  <div class="mt-4 pt-2" v-if="!readOnly">
                    <button type="button" class="btn btn-outline-danger me-1" @click="removeResponse(response)"
                            title="Elimina risposta">
                      <font-awesome-icon icon="fa-solid fa-xmark"></font-awesome-icon>
                    </button>
                    <button type="button" class="btn btn-outline-primary"
                            @click="currentQuestion.additionalProperties.select_values.push(cloneOptions(response))"
                            title="Duplica risposta"
                    >
                      <font-awesome-icon icon="fa-solid fa-copy"></font-awesome-icon>
                    </button>
                  </div>
                </div>

                <div class="alert alert-warning d-flex align-content-center align-items-center justify-content-between"
                     role="alert" v-else-if="confirmDeleteResponse && confirmDeleteResponse.value === response.value">
                  <span class="flex-grow-1">Attenzione: verrà eliminato tutto il ramo di domande collegato alla risposta <b>{{ response.label
                    }}</b>. Procedere?</span>
                  <button v-if="!readOnly" type="button" class="btn btn-secondary btn-sm me-2"
                          @click="removeResponse(response)" title="Elimina risposta">Elimina
                  </button>
                  <button v-if="!readOnly" type="button" class="btn btn-outline-secondary btn-sm"
                          @click="()=>{ confirmDeleteResponse = null; }" title="Annulla">Annulla
                  </button>
                </div>
              </div>
            </div>

            <label class="form-label">Risposte *
              <button v-if="!readOnly"
                      type="button"
                      class="btn btn-outline-primary btn-sm ms-2"
                      @click="addResponse"
              >
                <font-awesome-icon icon="fa-plus"></font-awesome-icon>
              </button>
            </label>
          </div>
        </div>

        <div v-if="pollTemplate && pollTemplate.state === 'active' && revisionAlert" class="alert alert-warning"
             role="alert">
          {{ revisionMessage }}
        </div>

      </div>
    </div>
    <div v-else class="d-flex justify-content-center py-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>


  <div class="d-flex justify-content-between mt-3">
    <button-torna-alla-lista @click="router.back()"></button-torna-alla-lista>
    <button-salva v-if="!readOnly" :loading="loading" :disabled="loading" @click="validateQuestion"></button-salva>
  </div>

</template>

<style>
</style>
